/**
 * Email Debug Panel Component
 * Displays debugging information for email linking and thread queries
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, RefreshCw, Mail, CheckCircle2, XCircle, Send } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useState } from "react";

interface EmailDebugPanelProps {
  contactId?: string | null;
  dealId?: string | null;
  contactEmail?: string | null;
}

export default function EmailDebugPanel({
  contactId,
  dealId,
  contactEmail,
}: EmailDebugPanelProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [testEmailTo, setTestEmailTo] = useState(contactEmail || "");

  // Fetch debug information
  const debugQuery = useQuery({
    queryKey: ["email-debug", contactId, dealId, contactEmail],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (contactId) params.append("contactId", contactId);
      if (dealId) params.append("dealId", dealId);
      if (contactEmail) params.append("email", contactEmail);

      const res = await fetch(apiUrl(`/api/email/debug?${params.toString()}`), {
        credentials: "include",
      });
      if (!res.ok) {
        let errorMessage = `HTTP ${res.status}: Failed to fetch debug info`;
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          // Include stack trace if available
          if (errorData.stack) {
            errorMessage += `\n\nStack: ${errorData.stack}`;
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = res.statusText || errorMessage;
        }
        const error = new Error(errorMessage);
        (error as any).status = res.status;
        (error as any).response = res;
        throw error;
      }
      return res.json();
    },
    enabled: !!(contactId || dealId || contactEmail),
  });

  // Test email sync mutation
  const testSyncMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(apiUrl("/api/email/test-sync"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          contactId: contactId || undefined,
          dealId: dealId || undefined,
          fromEmail: "test@example.com",
          toEmail: contactEmail || "test@example.com",
          subject: "Teszt email",
          body: "<p>Ez egy teszt email a linking logika teszteléséhez.</p>",
        }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to create test email");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-threads"] });
      qc.invalidateQueries({ queryKey: ["email-debug"] });
    },
  });

  // Test Resend API mutation
  const testResendMutation = useMutation({
    mutationFn: async (to: string) => {
      const res = await fetch(apiUrl("/api/email/test-resend"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          to,
          from: "onboarding@resend.dev",
          subject: "Teszt email - Resend API",
        }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || error.error || "Failed to send test email");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-debug"] });
    },
  });

  if (!contactId && !dealId && !contactEmail) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Nincs elérhető debug információ. Adj meg contact ID-t, deal ID-t vagy email címet.
        </AlertDescription>
      </Alert>
    );
  }

  if (debugQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Debug Információ</CardTitle>
          <CardDescription>Betöltés...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (debugQuery.isError) {
    const errorData = debugQuery.error as any;
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <div>
              <strong>Hiba történt a debug információk betöltésekor:</strong>
            </div>
            <div className="text-sm">
              {errorData?.message || (debugQuery.error instanceof Error ? debugQuery.error.message : "Ismeretlen hiba")}
            </div>
            {errorData?.stack && (
              <details className="text-xs mt-2">
                <summary className="cursor-pointer">Stack trace</summary>
                <pre className="mt-2 p-2 bg-background rounded overflow-auto">{errorData.stack}</pre>
              </details>
            )}
            {errorData?.queryParams && (
              <div className="text-xs mt-2">
                <strong>Query params:</strong> {JSON.stringify(errorData.queryParams)}
              </div>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const debug = debugQuery.data;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Email Debug Információ</CardTitle>
              <CardDescription>
                Diagnosztikai információk az email linking és query működéséről
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => debugQuery.refetch()}
              disabled={debugQuery.isFetching}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${debugQuery.isFetching ? "animate-spin" : ""}`} />
              Frissítés
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Contact Info */}
          {debug.contact && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Contact Információ
              </h4>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div>
                  <strong>ID:</strong> {debug.contact.id} ({debug.contact.idType})
                </div>
                <div>
                  <strong>Email:</strong> {debug.contact.email || "(nincs)"}
                </div>
                <div>
                  <strong>Név:</strong> {debug.contact.name || "(nincs)"}
                </div>
                {debug.contact.error && (
                  <div className="text-destructive">
                    <strong>Hiba:</strong> {debug.contact.error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deal Info */}
          {debug.deal && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Deal Információ
              </h4>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div>
                  <strong>ID:</strong> {debug.deal.id} ({debug.deal.idType})
                </div>
                <div>
                  <strong>Contact ID:</strong> {debug.deal.contactId || "(nincs)"} (
                  {debug.deal.contactIdType})
                </div>
                <div>
                  <strong>Cím:</strong> {debug.deal.title || "(nincs)"}
                </div>
                {debug.deal.error && (
                  <div className="text-destructive">
                    <strong>Hiba:</strong> {debug.deal.error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resend Configuration */}
          {debug.resendConfig && (
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                {debug.resendConfig.apiKeyConfigured ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600" />
                )}
                Resend Konfiguráció
              </h4>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <strong>API Key beállítva:</strong>
                  {debug.resendConfig.apiKeyConfigured ? (
                    <Badge variant="default" className="bg-green-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Igen
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Nem
                    </Badge>
                  )}
                </div>
                {debug.resendConfig.apiKeyConfigured && (
                  <>
                    <div>
                      <strong>API Key hossz:</strong> {debug.resendConfig.apiKeyLength} karakter
                    </div>
                    <div>
                      <strong>API Key prefix:</strong> {debug.resendConfig.apiKeyPrefix}
                    </div>
                  </>
                )}
                {!debug.resendConfig.apiKeyConfigured && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      A RESEND_API_KEY nincs beállítva! Az emailek nem küldődnek el. Állítsd be a Cloudflare Pages environment változókat.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {/* Email Status Statistics */}
          {debug.emailStatus && (
            <div>
              <h4 className="font-semibold mb-2">Email Státusz Statisztika</h4>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-green-100 dark:bg-green-900/20 p-3 rounded-md">
                  <div className="text-xs text-muted-foreground">Elküldve</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                    {debug.emailStatus.sent || 0}
                  </div>
                </div>
                <div className="bg-red-100 dark:bg-red-900/20 p-3 rounded-md">
                  <div className="text-xs text-muted-foreground">Sikertelen</div>
                  <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                    {debug.emailStatus.failed || 0}
                  </div>
                </div>
                <div className="bg-yellow-100 dark:bg-yellow-900/20 p-3 rounded-md">
                  <div className="text-xs text-muted-foreground">Várólistán</div>
                  <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                    {debug.emailStatus.queued || 0}
                  </div>
                </div>
                <div className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded-md">
                  <div className="text-xs text-muted-foreground">Fogadott</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                    {debug.emailStatus.received || 0}
                  </div>
                </div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-xs text-muted-foreground">Összes email</div>
                <div className="text-2xl font-bold">{debug.emailStatus.total || 0}</div>
              </div>
            </div>
          )}

          {/* Recent Emails with Errors */}
          {debug.recentEmails && debug.recentEmails.length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Legutóbbi Emailek</h4>
              <div className="space-y-2">
                {debug.recentEmails.map((email: any) => (
                  <div
                    key={email.id}
                    className={`p-3 rounded-md border ${
                      email.status === "failed"
                        ? "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800"
                        : email.status === "sent"
                        ? "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
                        : "bg-muted"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant={
                              email.status === "sent"
                                ? "default"
                                : email.status === "failed"
                                ? "destructive"
                                : "secondary"
                            }
                            className="text-xs"
                          >
                            {email.status === "sent" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {email.status === "failed" && <XCircle className="h-3 w-3 mr-1" />}
                            {email.status}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {email.direction === "outgoing" ? "→ Kimenő" : "← Bejövő"}
                          </Badge>
                        </div>
                        <div className="text-sm font-medium truncate">{email.subject || "(nincs tárgy)"}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {email.from || "(ismeretlen küldő)"}
                        </div>
                        {email.error && (
                          <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                            <strong>Hiba:</strong> {email.error}
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground mt-1">
                          {email.sentAt || email.createdAt
                            ? new Date(email.sentAt || email.createdAt).toLocaleString("hu-HU")
                            : "Nincs dátum"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Threads Count */}
          <div>
            <h4 className="font-semibold mb-2">Email Threads Statisztika</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-muted p-3 rounded-md">
                <div className="text-xs text-muted-foreground">Contact-hoz</div>
                <div className="text-2xl font-bold">{debug.emailThreads?.byContact || 0}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-xs text-muted-foreground">Deal-hez</div>
                <div className="text-2xl font-bold">{debug.emailThreads?.byDeal || 0}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-xs text-muted-foreground">Email címhez</div>
                <div className="text-2xl font-bold">{debug.emailThreads?.byEmail || 0}</div>
              </div>
              <div className="bg-muted p-3 rounded-md">
                <div className="text-xs text-muted-foreground">Összesen</div>
                <div className="text-2xl font-bold">{debug.emailThreads?.total || 0}</div>
              </div>
            </div>
          </div>

          {/* Linking Test */}
          {debug.linkingTest && (
            <div>
              <h4 className="font-semibold mb-2">Email Linking Teszt</h4>
              <div className="bg-muted p-3 rounded-md space-y-2 text-sm">
                <div>
                  <strong>Input email:</strong> {debug.linkingTest.inputEmail}
                </div>
                <div>
                  <strong>Normalizált email:</strong> {debug.linkingTest.normalizedEmail}
                </div>
                <div>
                  <strong>Találatok:</strong> {debug.linkingTest.contactsFound}
                </div>
                {debug.linkingTest.contacts && debug.linkingTest.contacts.length > 0 && (
                  <div>
                    <strong>Contact ID-k:</strong>{" "}
                    {debug.linkingTest.contacts.map((c: any) => c.id).join(", ")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Database Types */}
          {debug.databaseTypes && Object.keys(debug.databaseTypes).length > 0 && (
            <div>
              <h4 className="font-semibold mb-2">Adatbázis Típusok</h4>
              <div className="bg-muted p-3 rounded-md space-y-2 text-sm">
                {debug.databaseTypes.note && (
                  <div className="text-xs text-muted-foreground mb-2">{debug.databaseTypes.note}</div>
                )}
                {debug.databaseTypes.email_threads && (
                  <div>
                    <strong>email_threads:</strong>
                    <ul className="ml-4 mt-1 font-mono">
                      <li>contact_id: {debug.databaseTypes.email_threads.contact_id || "?"}</li>
                      <li>deal_id: {debug.databaseTypes.email_threads.deal_id || "?"}</li>
                    </ul>
                  </div>
                )}
                {debug.databaseTypes.contacts && (
                  <div className="font-mono">
                    <strong>contacts.id:</strong> {debug.databaseTypes.contacts.id || "?"}
                  </div>
                )}
                {debug.databaseTypes.deals && (
                  <div className="font-mono">
                    <strong>deals.id:</strong> {debug.databaseTypes.deals.id || "?"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Errors */}
          {debug.errors && debug.errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Hibák:</strong>
                <ul className="list-disc list-inside mt-2">
                  {debug.errors.map((err: string, i: number) => (
                    <li key={i} className="text-sm">{err}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Type Compatibility Warning */}
          {debug.databaseTypes?.email_threads?.contact_id &&
            debug.databaseTypes?.contacts?.id &&
            debug.databaseTypes.email_threads.contact_id !== debug.databaseTypes.contacts.id && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Típus inkompatibilitás!</strong> email_threads.contact_id (
                  {debug.databaseTypes.email_threads.contact_id}) és contacts.id (
                  {debug.databaseTypes.contacts.id}) típusa nem egyezik. Ez problémát okozhat a
                  linking során.
                </AlertDescription>
              </Alert>
            )}

          {/* Test Resend API */}
          {debug.resendConfig && (
            <div className="pt-4 border-t">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold mb-1 flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Resend API Tesztelés
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Teszteld közvetlenül a Resend API-t egy email küldésével. Ez segít azonosítani, hogy a probléma az API kulccsal, a domain verifikációval, vagy más okkal van-e.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder={t("emailExtra.emailPlaceholderExample")}
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    onClick={() => {
                      if (testEmailTo) {
                        testResendMutation.mutate(testEmailTo);
                      }
                    }}
                    disabled={testResendMutation.isPending || !testEmailTo}
                  >
                    {testResendMutation.isPending ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Küldés...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Küldés
                      </>
                    )}
                  </Button>
                </div>
                {testResendMutation.isSuccess && (
                  <Alert className="mt-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <div><strong>Sikeres!</strong> Email elküldve a Resend API-n keresztül.</div>
                        {testResendMutation.data?.messageId && (
                          <div className="text-xs">Message ID: {testResendMutation.data.messageId}</div>
                        )}
                        <div className="text-xs text-muted-foreground mt-2">
                          Ellenőrizd a bejövő email mappádat (és a spam mappát is)!
                        </div>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                {testResendMutation.isError && (() => {
                  const mutationData = (testResendMutation as { data?: { testDetails?: { apiKeyConfigured?: boolean; apiKeyLength?: number } } }).data;
                  const testDetails = mutationData?.testDetails;
                  return (
                  <Alert variant="destructive" className="mt-2">
                    <XCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <div><strong>Hiba történt:</strong></div>
                        <div className="text-sm">
                          {testResendMutation.error instanceof Error ? testResendMutation.error.message : "Ismeretlen hiba"}
                        </div>
                        {testDetails && (
                          <div className="text-xs mt-2 space-y-1">
                            <div>API Key beállítva: {testDetails.apiKeyConfigured ? "Igen" : "Nem"}</div>
                            <div>API Key hossz: {testDetails.apiKeyLength ?? 0} karakter</div>
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Test Email Button */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold mb-1">Teszt Email Létrehozása</h4>
                <p className="text-sm text-muted-foreground">
                  Létrehoz egy teszt email thread-et és email-t a linking logika teszteléséhez.
                </p>
              </div>
              <Button
                onClick={() => testSyncMutation.mutate()}
                disabled={testSyncMutation.isPending}
              >
                {testSyncMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Létrehozás...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Teszt Email
                  </>
                )}
              </Button>
            </div>
            {testSyncMutation.isSuccess && (
              <Alert className="mt-2">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Teszt email sikeresen létrehozva! Thread ID: {testSyncMutation.data?.threadId}
                </AlertDescription>
              </Alert>
            )}
            {testSyncMutation.isError && (
              <Alert variant="destructive" className="mt-2">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  Hiba: {testSyncMutation.error instanceof Error ? testSyncMutation.error.message : "Ismeretlen hiba"}
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

